import type {
  IntegrationTriggerField,
  IntegrationTriggerPayloadSchema,
  TriggerPayloadFieldPiiLevel,
  TriggerPrimitiveType,
} from '@/lib/triggers/types'
import {
  SUPPORTED_STRIPE_EVENT_SET,
  SUPPORTED_STRIPE_EVENTS,
  type SupportedStripeEventType,
} from './event-types'
import {
  CHARGE_REFUNDED_EXTRAS,
  CHECKOUT_EXTRAS,
  CUSTOMER_DELETED_EXTRAS,
  INVOICE_BASIC_EXTRAS,
  INVOICE_CREATED_EXTRAS,
  INVOICE_UPCOMING_EXTRAS,
  NO_EXTRAS,
  PAYMENT_INTENT_FAILED_EXTRAS,
  QUOTE_EXTRAS,
  REFUND_CREATED_EXTRAS,
  SUBSCRIPTION_BASIC_EXTRAS,
  SUBSCRIPTION_UPDATED_EXTRAS,
} from './stripe-event-extras'

export {
  SUPPORTED_STRIPE_EVENT_SET,
  SUPPORTED_STRIPE_EVENTS,
  type SupportedStripeEventType,
} from './event-types'

/**
 * Rich field shape for Stripe event documentation. `description` and
 * `exampleValue` ride along for inline doc/DX value, but they are
 * stripped before persisting to Convex via
 * {@link import('@/lib/triggers/types').toCanonicalPayloadSchema}.
 */
export type TriggerField = IntegrationTriggerField

export type TriggerPayloadSchema = IntegrationTriggerPayloadSchema

export type StripeEventCategory =
  | 'checkout'
  | 'customer'
  | 'subscription'
  | 'invoice'
  | 'quote'
  | 'payment'

/**
 * @deprecated The per-event fire gate was retired entirely (May 2026
 * one-switch refactor — see `docs/INTEGRATION_TRIGGERS_REFACTOR.md`).
 * Fire is now governed solely by `automation.published`. This type
 * is kept as a back-compat alias for callers that still serialize
 * the legacy four-field shape (e.g. the test harness mutation).
 * For all new code, use the slim `StripeEventBehaviorState` shape from
 * `lib/data/convex/stripeEventConfigs.ts` (just `syncContactName`).
 */
export type StripeEventConfigState = {
  eventType: SupportedStripeEventType
  enabled: boolean
  syncContactName: boolean
  triggerLoop: boolean
}

export type StripeSupportedEventMetadata = {
  eventType: SupportedStripeEventType
  title: string
  description: string
  category: StripeEventCategory
  /**
   * Rich schema retained on the source-of-truth catalog so the
   * `internal: true` flag survives down to `toCanonicalPayloadSchema`.
   * Trigger provisioning canonicalises this before persisting to
   * Convex, which strips the source-only fields (`description`,
   * `exampleValue`) AND any field marked `internal`.
   */
  payloadSchema: IntegrationTriggerPayloadSchema
  samplePayload: Record<string, string | number | boolean | null>
}

// =============================================================
// Type machinery: derive per-event payload TS types from `extras`
// =============================================================

/**
 * `ExtrasField` mirrors `IntegrationTriggerField` MINUS the `key`
 * (the surrounding map's key is the field name) and lets every
 * property be `readonly` so the per-event `as const` consts
 * type-check against this shape. The `key` is reattached at
 * `buildPayloadSchema` time when projecting back to the flat
 * `TriggerField` array.
 */
type ExtrasField = {
  readonly type: TriggerPrimitiveType
  readonly required: boolean
  readonly fallbackValue?: string | number | boolean
  readonly pii?: TriggerPayloadFieldPiiLevel
  readonly description?: string
  readonly exampleValue?: string | number | boolean
  /**
   * When `true`, the field still flows through the extractor (so the
   * TypeScript `StripeEventPayload<E>` shape stays accurate and the
   * contact-sync mapper can read it from the raw payload) but
   * `toCanonicalPayloadSchema` filters it OUT of the user-facing
   * `payloadSchema` written to Convex. Use for opaque IDs that have no
   * value as a template variable (`subscriptionId`, `invoiceId`,
   * `checkoutSessionId`, etc.).
   */
  readonly internal?: boolean
}

/**
 * `ExtrasMap` describes the runtime shape of an event's "extras" —
 * the per-event payload fields ON TOP OF `COMMON_FIELDS`. The shape
 * is `readonly`-tolerant so the per-event `as const` consts (which
 * preserve literal types for the mapped TS payload derivation)
 * remain assignable.
 */
type ExtrasMap = Record<string, ExtrasField>

type PrimitiveToTS<T extends TriggerPrimitiveType> = T extends 'string'
  ? string
  : T extends 'int'
    ? number
    : T extends 'boolean'
      ? boolean
      : never

type RequiredKeys<E extends ExtrasMap> = {
  [K in keyof E]: E[K]['required'] extends true ? K : never
}[keyof E]

type OptionalKeys<E extends ExtrasMap> = {
  [K in keyof E]: E[K]['required'] extends true ? never : K
}[keyof E]

type ExtrasShape<E extends ExtrasMap> = {
  [K in RequiredKeys<E>]: PrimitiveToTS<E[K]['type']>
} & {
  [K in OptionalKeys<E>]?: PrimitiveToTS<E[K]['type']>
}

/**
 * Compile-time shape of the fields the Stripe extractors return. This
 * is the RAW extractor output (persisted to `triggerInstances.payload`
 * and consumed by the Stripe contact mapper) — broader than the slim
 * user-facing `COMMON_FIELDS` runtime array because the schema-filtered
 * `resolvedPayload` flows through `validateAndResolvePayload` which
 * strips anything not declared in `payloadSchema.fields`.
 *
 * `email` is REQUIRED on every event (matches the cross-provider
 * invariant enforced by `convex/triggerEvents/v1Mutations.ts`
 * `assertEmailRequiredField`). The extractor synthesises
 * `unknown+<seed>@<orgId>.brew.local` for events that have no real
 * email on the wire (mirrors Clerk's `organization.created`).
 *
 * Stripe-specific metadata (`stripeEventId`, `stripeEventType`,
 * `stripeAccountId`, `livemode`, `objectId`, `objectType`,
 * `customerId`) still flows through the extractor return so the contact
 * mapper can land `stripe_customer_id` etc. on synced contacts, but the
 * slim user-facing `COMMON_FIELDS` array intentionally omits them so
 * the Test dialog + template surface stay free of opaque IDs.
 */
type CommonFieldsTS = {
  stripeEventId: string
  stripeEventType: string
  livemode: boolean
  email: string
  stripeAccountId?: string
  objectId?: string
  objectType?: string
  customerId?: string
  firstName?: string
  lastName?: string
  amount?: number
  currency?: string
  status?: string
  created?: number
}

/**
 * Build the full per-event payload TypeScript type from an `extras`
 * spec. Used by extractor signatures and by external callers that
 * want to read the typed shape of a specific Stripe event:
 *
 *   const payload: StripeEventPayload<'invoice.created'> = ...
 *   // TS rejects `subscriptionId` here — `invoice.created` doesn't declare it.
 */
export type StripeEventPayloadFor<E extends ExtrasMap> = CommonFieldsTS &
  ExtrasShape<E>

// =============================================================
// Common fields (used by every event)
// =============================================================

/**
 * The 14 fields every Stripe event carries. Provider-metadata fields
 * (`stripeEventId`, `stripeEventType`, `stripeAccountId`, `livemode`,
 * `objectId`, `objectType`, `customerId`) are marked `internal: true` —
 * they stay on the raw extractor output (so the per-event typed
 * `StripeEventPayload<E>` shape and the contact-sync mapper continue
 * working) but `toCanonicalPayloadSchema` filters them out before the
 * row is persisted to Convex. End result: the user-facing Test dialog
 * + merge-tag picker + `validateAndResolvePayload` only ever see the
 * marketing-actionable fields (`email`, name, amount, currency,
 * status, created).
 */
const COMMON_FIELDS: Array<TriggerField> = [
  {
    key: 'stripeEventId',
    type: 'string',
    required: true,
    internal: true,
    description: 'The Stripe webhook event ID.',
    exampleValue: 'evt_123',
  },
  {
    key: 'stripeEventType',
    type: 'string',
    required: true,
    internal: true,
    description: 'The Stripe webhook event type.',
    exampleValue: 'customer.created',
  },
  {
    key: 'stripeAccountId',
    type: 'string',
    required: false,
    internal: true,
    description: 'Connected Stripe account ID.',
    exampleValue: 'acct_123',
  },
  {
    key: 'objectId',
    type: 'string',
    required: false,
    internal: true,
    description: 'The primary Stripe object ID from event.data.object.',
    exampleValue: 'cus_123',
  },
  {
    key: 'objectType',
    type: 'string',
    required: false,
    internal: true,
    description: 'The Stripe object type from event.data.object.object.',
    exampleValue: 'customer',
  },
  {
    key: 'customerId',
    type: 'string',
    required: false,
    internal: true,
    description: 'Stripe customer ID when available.',
    exampleValue: 'cus_123',
  },
  {
    key: 'email',
    type: 'string',
    required: true,
    pii: 'high' satisfies TriggerPayloadFieldPiiLevel,
    description:
      'Recipient email address resolved from the Stripe event. Synthetic placeholder `unknown+<seed>@<orgId>.brew.local` when the event carries no real email (matches the cross-provider invariant on `convex/triggerEvents/v1Mutations.ts:assertEmailRequiredField`).',
    exampleValue: 'alex@example.com',
  },
  {
    key: 'firstName',
    type: 'string',
    required: false,
    pii: 'high' satisfies TriggerPayloadFieldPiiLevel,
    description: 'Customer first name when available.',
    exampleValue: 'Alex',
  },
  {
    key: 'lastName',
    type: 'string',
    required: false,
    pii: 'high' satisfies TriggerPayloadFieldPiiLevel,
    description: 'Customer last name when available.',
    exampleValue: 'Morgan',
  },
  {
    key: 'amount',
    type: 'int',
    required: false,
    description:
      'Amount in the Stripe event payload, in the smallest currency unit.',
    exampleValue: 4900,
  },
  {
    key: 'currency',
    type: 'string',
    required: false,
    description: 'Three-letter ISO currency code.',
    exampleValue: 'usd',
  },
  {
    key: 'status',
    type: 'string',
    required: false,
    description: 'Primary status from the Stripe object.',
    exampleValue: 'active',
  },
  {
    key: 'livemode',
    type: 'boolean',
    required: true,
    internal: true,
    description: 'Whether the event came from live mode.',
    exampleValue: false,
  },
  {
    key: 'created',
    type: 'int',
    required: false,
    description: 'Unix timestamp from the Stripe object when available.',
    exampleValue: 1_710_000_000,
  },
]

// =============================================================
// Single source of truth: STRIPE_EVENT_DEFINITIONS
//
// One entry per supported event type. The `extras` const drives
// BOTH the runtime payloadSchema AND the compile-time
// `StripeEventPayload<E>` type — so the per-event extractor in
// `data-in/extractors.ts` is type-checked against the schema
// without any runtime dependency.
// =============================================================

type StripeEventDefinition<E extends ExtrasMap = ExtrasMap> = {
  title: string
  description: string
  category: StripeEventCategory
  extras: E
  sample: Record<string, string | number | boolean | null>
}

export const STRIPE_EVENT_DEFINITIONS = {
  'checkout.session.async_payment_succeeded': {
    title: 'Stripe - Checkout Async Payment Succeeded',
    description:
      'Fires when a Stripe Checkout session succeeds after an asynchronous payment method settles.',
    category: 'checkout',
    extras: CHECKOUT_EXTRAS,
    sample: {
      objectId: 'cs_test_123',
      objectType: 'checkout.session',
      checkoutSessionId: 'cs_test_123',
      customerId: 'cus_test_123',
      email: 'checkout@example.com',
      firstName: 'Checkout',
      lastName: 'Customer',
      amount: 4900,
      currency: 'usd',
      status: 'complete',
      paymentStatus: 'paid',
      sessionMode: 'payment',
      created: 1_710_000_000,
    },
  },
  'checkout.session.async_payment_failed': {
    title: 'Stripe - Checkout Async Payment Failed',
    description:
      'Fires when a Stripe Checkout session fails after an asynchronous payment method is rejected.',
    category: 'checkout',
    extras: CHECKOUT_EXTRAS,
    sample: {
      objectId: 'cs_test_123',
      objectType: 'checkout.session',
      checkoutSessionId: 'cs_test_123',
      customerId: 'cus_test_123',
      email: 'checkout@example.com',
      firstName: 'Checkout',
      lastName: 'Customer',
      amount: 4900,
      currency: 'usd',
      status: 'expired',
      paymentStatus: 'unpaid',
      sessionMode: 'payment',
      created: 1_710_000_000,
    },
  },
  'checkout.session.completed': {
    title: 'Stripe - Checkout Completed',
    description: 'Fires when a Stripe Checkout session completes successfully.',
    category: 'checkout',
    extras: CHECKOUT_EXTRAS,
    sample: {
      objectId: 'cs_test_123',
      objectType: 'checkout.session',
      checkoutSessionId: 'cs_test_123',
      customerId: 'cus_test_123',
      email: 'checkout@example.com',
      firstName: 'Checkout',
      lastName: 'Customer',
      amount: 4900,
      currency: 'usd',
      status: 'complete',
      paymentStatus: 'paid',
      sessionMode: 'subscription',
      created: 1_710_000_000,
    },
  },
  'customer.created': {
    title: 'Stripe - Customer Created',
    description:
      'Fires when a Stripe customer is created and can be synced into Brew contacts.',
    category: 'customer',
    extras: NO_EXTRAS,
    sample: {
      objectId: 'cus_test_123',
      objectType: 'customer',
      customerId: 'cus_test_123',
      email: 'customer@example.com',
      firstName: 'Alex',
      lastName: 'Morgan',
      status: 'active',
      created: 1_710_000_000,
    },
  },
  'customer.updated': {
    title: 'Stripe - Customer Updated',
    description:
      'Fires when an existing Stripe customer record changes and Brew should refresh contact data.',
    category: 'customer',
    extras: NO_EXTRAS,
    sample: {
      objectId: 'cus_test_123',
      objectType: 'customer',
      customerId: 'cus_test_123',
      email: 'customer@example.com',
      firstName: 'Alex',
      lastName: 'Morgan',
      status: 'active',
      created: 1_710_000_000,
    },
  },
  'customer.deleted': {
    title: 'Stripe - Customer Deleted',
    description:
      'Fires when a Stripe customer is deleted and Brew should unsubscribe or remove the matching contact.',
    category: 'customer',
    extras: CUSTOMER_DELETED_EXTRAS,
    sample: {
      objectId: 'cus_test_123',
      objectType: 'customer',
      customerId: 'cus_test_123',
      email: 'customer@example.com',
      deleted: true,
      created: 1_710_000_000,
    },
  },
  'customer.subscription.created': {
    title: 'Stripe - Subscription Created',
    description: 'Fires when a customer starts a Stripe subscription.',
    category: 'subscription',
    extras: SUBSCRIPTION_BASIC_EXTRAS,
    sample: {
      objectId: 'sub_test_123',
      objectType: 'subscription',
      subscriptionId: 'sub_test_123',
      customerId: 'cus_test_123',
      email: 'subscriber@example.com',
      firstName: 'Taylor',
      lastName: 'Subscriber',
      amount: 9900,
      currency: 'usd',
      status: 'active',
      created: 1_710_000_000,
    },
  },
  'customer.subscription.updated': {
    title: 'Stripe - Subscription Updated',
    description:
      'Fires when a Stripe subscription changes (plan upgrade, downgrade, quantity change, status transition). Use for tier-change announcements, win-back on downgrade, or dunning hand-off when status flips to past_due.',
    category: 'subscription',
    extras: SUBSCRIPTION_UPDATED_EXTRAS,
    sample: {
      objectId: 'sub_test_123',
      objectType: 'subscription',
      subscriptionId: 'sub_test_123',
      customerId: 'cus_test_123',
      email: 'subscriber@example.com',
      firstName: 'Taylor',
      lastName: 'Subscriber',
      amount: 14_900,
      currency: 'usd',
      status: 'active',
      previousPlan: 'price_starter_monthly',
      newPlan: 'price_pro_monthly',
      previousQuantity: 1,
      newQuantity: 5,
      previousStatus: 'trialing',
      created: 1_710_000_000,
    },
  },
  'customer.subscription.paused': {
    title: 'Stripe - Subscription Paused',
    description: 'Fires when a Stripe subscription is paused.',
    category: 'subscription',
    extras: SUBSCRIPTION_BASIC_EXTRAS,
    sample: {
      objectId: 'sub_test_123',
      objectType: 'subscription',
      subscriptionId: 'sub_test_123',
      customerId: 'cus_test_123',
      email: 'subscriber@example.com',
      amount: 9900,
      currency: 'usd',
      status: 'paused',
      created: 1_710_000_000,
    },
  },
  'customer.subscription.resumed': {
    title: 'Stripe - Subscription Resumed',
    description: 'Fires when a paused Stripe subscription resumes.',
    category: 'subscription',
    extras: SUBSCRIPTION_BASIC_EXTRAS,
    sample: {
      objectId: 'sub_test_123',
      objectType: 'subscription',
      subscriptionId: 'sub_test_123',
      customerId: 'cus_test_123',
      email: 'subscriber@example.com',
      amount: 9900,
      currency: 'usd',
      status: 'active',
      created: 1_710_000_000,
    },
  },
  'customer.subscription.trial_will_end': {
    title: 'Stripe - Subscription Trial Will End',
    description: 'Fires shortly before a Stripe subscription trial ends.',
    category: 'subscription',
    extras: SUBSCRIPTION_BASIC_EXTRAS,
    sample: {
      objectId: 'sub_test_123',
      objectType: 'subscription',
      subscriptionId: 'sub_test_123',
      customerId: 'cus_test_123',
      email: 'subscriber@example.com',
      amount: 9900,
      currency: 'usd',
      status: 'trialing',
      created: 1_710_000_000,
    },
  },
  'customer.subscription.deleted': {
    title: 'Stripe - Subscription Deleted',
    description: 'Fires when a Stripe subscription is canceled or deleted.',
    category: 'subscription',
    extras: SUBSCRIPTION_BASIC_EXTRAS,
    sample: {
      objectId: 'sub_test_123',
      objectType: 'subscription',
      subscriptionId: 'sub_test_123',
      customerId: 'cus_test_123',
      email: 'subscriber@example.com',
      amount: 9900,
      currency: 'usd',
      status: 'canceled',
      created: 1_710_000_000,
    },
  },
  'invoice.created': {
    title: 'Stripe - Invoice Created',
    description:
      'Fires when Stripe creates a draft invoice. Use for first-touch billing emails on custom invoicing flows.',
    category: 'invoice',
    extras: INVOICE_CREATED_EXTRAS,
    sample: {
      objectId: 'in_test_123',
      objectType: 'invoice',
      invoiceId: 'in_test_123',
      invoiceNumber: '0001',
      billingReason: 'subscription_create',
      customerId: 'cus_test_123',
      email: 'billing@example.com',
      amount: 9900,
      currency: 'usd',
      status: 'draft',
      created: 1_710_000_000,
    },
  },
  'invoice.paid': {
    title: 'Stripe - Invoice Paid',
    description: 'Fires when a Stripe invoice is paid successfully.',
    category: 'invoice',
    extras: INVOICE_BASIC_EXTRAS,
    sample: {
      objectId: 'in_test_123',
      objectType: 'invoice',
      invoiceId: 'in_test_123',
      invoiceNumber: '0001',
      customerId: 'cus_test_123',
      email: 'billing@example.com',
      amount: 9900,
      currency: 'usd',
      status: 'paid',
      created: 1_710_000_000,
    },
  },
  'invoice.payment_failed': {
    title: 'Stripe - Invoice Payment Failed',
    description: 'Fires when Stripe fails to collect payment for an invoice.',
    category: 'invoice',
    extras: INVOICE_BASIC_EXTRAS,
    sample: {
      objectId: 'in_test_123',
      objectType: 'invoice',
      invoiceId: 'in_test_123',
      invoiceNumber: '0001',
      customerId: 'cus_test_123',
      email: 'billing@example.com',
      amount: 9900,
      currency: 'usd',
      status: 'open',
      created: 1_710_000_000,
    },
  },
  'invoice.upcoming': {
    title: 'Stripe - Invoice Upcoming',
    description:
      'Fires before Stripe creates the next invoice for a customer subscription.',
    category: 'invoice',
    extras: INVOICE_UPCOMING_EXTRAS,
    sample: {
      objectId: 'in_test_123',
      objectType: 'invoice',
      invoiceId: 'in_test_123',
      customerId: 'cus_test_123',
      email: 'billing@example.com',
      amount: 9900,
      currency: 'usd',
      status: 'draft',
      created: 1_710_000_000,
    },
  },
  'quote.finalized': {
    title: 'Stripe - Quote Finalized',
    description: 'Fires when a Stripe quote is finalized and ready to send.',
    category: 'quote',
    extras: QUOTE_EXTRAS,
    sample: {
      objectId: 'qt_test_123',
      objectType: 'quote',
      quoteId: 'qt_test_123',
      customerId: 'cus_test_123',
      email: 'quote@example.com',
      amount: 14_900,
      currency: 'usd',
      status: 'open',
      created: 1_710_000_000,
    },
  },
  'quote.accepted': {
    title: 'Stripe - Quote Accepted',
    description: 'Fires when a Stripe quote is accepted by the customer.',
    category: 'quote',
    extras: QUOTE_EXTRAS,
    sample: {
      objectId: 'qt_test_123',
      objectType: 'quote',
      quoteId: 'qt_test_123',
      customerId: 'cus_test_123',
      email: 'quote@example.com',
      amount: 14_900,
      currency: 'usd',
      status: 'accepted',
      created: 1_710_000_000,
    },
  },
  'quote.canceled': {
    title: 'Stripe - Quote Canceled',
    description: 'Fires when a Stripe quote is canceled.',
    category: 'quote',
    extras: QUOTE_EXTRAS,
    sample: {
      objectId: 'qt_test_123',
      objectType: 'quote',
      quoteId: 'qt_test_123',
      customerId: 'cus_test_123',
      email: 'quote@example.com',
      amount: 14_900,
      currency: 'usd',
      status: 'canceled',
      created: 1_710_000_000,
    },
  },
  'charge.refunded': {
    title: 'Stripe - Charge Refunded',
    description:
      'Fires when a Stripe charge is refunded (fully or partially). Carries charge-level totals only. For refund-specific details (refund id, reason, status), listen to `refund.created` instead — per Stripe docs (https://docs.stripe.com/refunds#refund-events).',
    category: 'payment',
    extras: CHARGE_REFUNDED_EXTRAS,
    sample: {
      objectId: 'ch_test_123',
      objectType: 'charge',
      chargeId: 'ch_test_123',
      amountRefunded: 4900,
      customerId: 'cus_test_123',
      email: 'payments@example.com',
      amount: 4900,
      currency: 'usd',
      status: 'succeeded',
      created: 1_710_000_000,
    },
  },
  'refund.created': {
    title: 'Stripe - Refund Created',
    description:
      'Fires when a Stripe refund is created. Carries refund-specific details (refund id, reason, status). Use for refund acknowledgment emails, dispute-prevention flows, and any automation that needs to know about a SPECIFIC refund (vs. the charge-level totals on `charge.refunded`).',
    category: 'payment',
    extras: REFUND_CREATED_EXTRAS,
    sample: {
      objectId: 're_test_123',
      objectType: 'refund',
      refundId: 're_test_123',
      chargeId: 'ch_test_123',
      paymentIntentId: 'pi_test_123',
      refundAmount: 4900,
      refundCurrency: 'usd',
      refundReason: 'requested_by_customer',
      refundStatus: 'succeeded',
      customerId: 'cus_test_123',
      email: 'payments@example.com',
      amount: 4900,
      currency: 'usd',
      status: 'succeeded',
      created: 1_710_000_000,
    },
  },
  'payment_intent.payment_failed': {
    title: 'Stripe - Payment Failed',
    description:
      'Fires when a one-time payment intent fails. Use for non-subscription dunning / recovery flows. The `declineCode` extras field (when present) is the most actionable signal — it lets you write per-decline-reason recovery copy.',
    category: 'payment',
    extras: PAYMENT_INTENT_FAILED_EXTRAS,
    sample: {
      objectId: 'pi_test_123',
      objectType: 'payment_intent',
      paymentIntentId: 'pi_test_123',
      failureCode: 'card_declined',
      failureMessage: 'Your card was declined.',
      declineCode: 'insufficient_funds',
      customerId: 'cus_test_123',
      email: 'payments@example.com',
      amount: 4900,
      currency: 'usd',
      status: 'requires_payment_method',
      created: 1_710_000_000,
    },
  },
} as const satisfies Record<SupportedStripeEventType, StripeEventDefinition>

/**
 * Per-event payload TypeScript type. Indexed against
 * `STRIPE_EVENT_DEFINITIONS[E].extras` so adding / removing a key in
 * the `*_EXTRAS` const is the single change required to update the
 * type. The extractor map in `data-in/extractors.ts` consumes this
 * to make schema/processor drift a compile error.
 *
 *   const payload: StripeEventPayload<'invoice.created'> = ...
 *   // OK: payload.invoiceId (required)
 *   // OK: payload.billingReason (optional)
 *   // ERROR: payload.subscriptionId (not declared on invoice.created)
 */
export type StripeEventPayload<E extends SupportedStripeEventType> =
  StripeEventPayloadFor<(typeof STRIPE_EVENT_DEFINITIONS)[E]['extras']>

// =============================================================
// Derived runtime arrays (back-compat with existing consumers)
// =============================================================

function buildPayloadSchema(
  extras: ExtrasMap
): IntegrationTriggerPayloadSchema {
  return {
    type: 'object',
    fields: [
      ...COMMON_FIELDS,
      ...Object.entries(extras).map(([key, spec]) => ({
        key,
        type: spec.type,
        required: spec.required,
        ...(spec.fallbackValue !== undefined
          ? { fallbackValue: spec.fallbackValue }
          : {}),
        ...(spec.pii !== undefined ? { pii: spec.pii } : {}),
        ...(spec.internal !== undefined ? { internal: spec.internal } : {}),
        ...(spec.description !== undefined
          ? { description: spec.description }
          : {}),
        ...(spec.exampleValue !== undefined
          ? { exampleValue: spec.exampleValue }
          : {}),
      })),
    ],
  }
}

function buildSamplePayload(
  eventType: SupportedStripeEventType,
  payload: Record<string, string | number | boolean | null>
) {
  return {
    stripeEventId: 'evt_test_123',
    stripeEventType: eventType,
    stripeAccountId: 'acct_test_123',
    livemode: false,
    ...payload,
  }
}

export const STRIPE_EVENT_METADATA: Array<StripeSupportedEventMetadata> =
  SUPPORTED_STRIPE_EVENTS.map((eventType) => {
    const def = STRIPE_EVENT_DEFINITIONS[eventType]
    return {
      eventType,
      title: def.title,
      description: def.description,
      category: def.category,
      payloadSchema: buildPayloadSchema(def.extras),
      samplePayload: buildSamplePayload(eventType, def.sample),
    }
  })

export const STRIPE_EVENT_METADATA_BY_TYPE = new Map<
  SupportedStripeEventType,
  StripeSupportedEventMetadata
>(STRIPE_EVENT_METADATA.map((entry) => [entry.eventType, entry]))

export function isSupportedStripeEvent(
  eventType: string
): eventType is SupportedStripeEventType {
  return SUPPORTED_STRIPE_EVENT_SET.has(eventType)
}

export function getStripeEventMetadata(eventType: SupportedStripeEventType) {
  return STRIPE_EVENT_METADATA_BY_TYPE.get(eventType)
}

/**
 * @deprecated Used only by the Stripe trigger-provisioning helper to
 * seed the connect-time row catalogue. New code should not depend
 * on `enabled` / `triggerLoop` — they're vestigial after the May
 * 2026 one-switch refactor. Whether emails actually fire is gated
 * solely by `automation.published`. Kept around because dropping it
 * would cascade into a wider refactor outside this PR's scope.
 */
export function getDefaultStripeEventConfigState(
  eventType: SupportedStripeEventType
): StripeEventConfigState {
  return {
    eventType,
    enabled: false,
    syncContactName: false,
    triggerLoop: true,
  }
}

export function getDefaultStripeEventConfigStates(): Array<StripeEventConfigState> {
  return SUPPORTED_STRIPE_EVENTS.map(getDefaultStripeEventConfigState)
}

export function groupStripeEventMetadataByCategory() {
  return STRIPE_EVENT_METADATA.reduce(
    (acc, item) => {
      acc[item.category].push(item)
      return acc
    },
    {
      checkout: [] as Array<StripeSupportedEventMetadata>,
      customer: [] as Array<StripeSupportedEventMetadata>,
      subscription: [] as Array<StripeSupportedEventMetadata>,
      invoice: [] as Array<StripeSupportedEventMetadata>,
      quote: [] as Array<StripeSupportedEventMetadata>,
      payment: [] as Array<StripeSupportedEventMetadata>,
    }
  )
}
